import Vue from "vue";

import { BASE_API_URL } from "@/common/urls";
import axios from "axios";
axios.defaults.withCredentials = true;

import { removeElementFromArray } from "@/common/utils.array";
import { unwrapError } from "@/common/utils.exception";

import cache from "@/store/cache.js";

const vue = new Vue();

function defaultUser() {
  return {
    id: 0,
    zone: (new Date().getTimezoneOffset() / 60) * -1,
    name: "",
    email: "",
    active_board: 0,
    riot_name: ""
  };
}

function defaultState() {
  return {
    active: defaultUser(),
    all: [],
    loaded: false
  };
}
// state
const state = defaultState();

// getters
const getters = {
  id(state) {
    return state.active.id;
  },
  isAuthed(state) {
    if (!state.active || !state.active.id || state.active.id < 1) return false;
    return true;
  },
  isMy: state => entity => {
    if (entity && entity.user_id) {
      return entity.user_id == state.active.id;
    }
    return false;
  }
};

// actions
const actions = {
  clear({ state }) {
    const dState = defaultState();
    dState.active = state.active;
    dState.loaded = state.loaded;

    Object.assign(state, dState);
  },
  async startup({ dispatch }) {
    const userRaw = localStorage.getItem("user");
    const user = userRaw ? JSON.parse(userRaw) : defaultUser();
    await dispatch("setDelatelUser", user);
    dispatch("tryGetUser");
  },
  async getUser({ dispatch }) {
    return axios({
      method: "get",
      url: `${BASE_API_URL}/user`
    })
      .then(response => {
        const user = response.data;
        dispatch("setDelatelUser", user);
      })
      .catch(error => {
        dispatch("setDelatelUser", defaultUser());
        throw error;
      });
  },
  tryGetUser({ dispatch }) {
    return dispatch("getUser").catch(() => {});
  },
  async signin({ dispatch }, { name, password }) {
    try {
      const params = new URLSearchParams();
      params.append("name", name);
      params.append("password", password);
      await axios({
        method: "post",
        url: `${BASE_API_URL}/user/signin`,
        data: params
      });
      await dispatch("getUser");
    } catch (err) {
      console.log("failed to signin", err);
      throw unwrapError(err);
    }
  },
  signout({ dispatch }) {
    axios({
      method: "get",
      url: `${BASE_API_URL}/user/signout`
    })
      .then(() => {
        dispatch("setDelatelUser", null);
      })
      .catch(err => {
        console.error("Signout failed", err);
        dispatch("setDelatelUser", null);
      });
  },
  socket_user({ commit }, data) {
    if (data.user) {
      commit("ADD", data.user);

      commit("SET", data.user);
      commit("theme/SET_THEME", data.user.theme, { root: true });
    }
  },
  socket_users({ commit }, data) {
    if (data.users) {
      commit("ADD_RANGE", data.users);
    }
    if (data.boards_users) {
      commit(
        "ADD_RANGE_REL",
        data.boards_users.map(r => {
          return {
            board: cache.boards.byId(r.board_id),
            user: cache.users.byId(r.user_id)
          };
        })
      );
    }
  },
  async setDelatelUser({ state, dispatch, commit }, user) {
    const oldId = state.active ? state.active.id : null;

    commit("SET", user);

    Vue.set(state, "active", user);

    if (!user || oldId != user.id) {
      //reconnect with updated auth info
      vue.$socket.disconnect();
      vue.$socket.connect();

      await dispatch("clear", {}, { root: true });
    }

    state.loaded = true;
  }
};

function add(state, user) {
  if (!user || !user.id) return;

  if (cache.users.byId(user.id)) return;

  fixActiveBoard(user);
  state.all.push(user);
}

function addRel(state, board, user) {
  if (!board || !board.id) return;
  if (!user || !user.id) return;

  add(state, user);
  if (board.users.find(u => u.id == user.id)) {
    //already added
    return;
  }

  board.users.push(cache.users.byId(user.id));
}

function deleteRel(state, board, user) {
  if (!board || !board.id) return;
  if (!user || !user.id) return;

  const existing = board.users.find(u => u.id == user.id);
  if (!existing) {
    //already removed
    return;
  }

  removeElementFromArray(board.users, existing);
}

function fixActiveBoard(user) {
  if (user && user.active_board) {
    if (typeof user.active_board === "string") {
      user.active_board = parseInt(user.active_board);
      if (!user.active_board) {
        user.active_board = 0;
      }
    }
  }
}

// mutations
const mutations = {
  SET(state, user) {
    fixActiveBoard(user);
    localStorage.setItem("user", user ? JSON.stringify(user) : null);

    if (!user || !user.id) {
      console.log("user not set");
      state.active = defaultUser();
      state.loaded = true;
      return;
    }

    if (!user.zone) {
      user.zone = (new Date().getTimezoneOffset() / 60) * -1;
    }

    state.active = user;
  },
  ADD_RANGE(state, items) {
    items.forEach(user => {
      add(state, user);
    });
  },
  ADD(state, user) {
    add(state, user);
  },
  ADD_RANGE_REL(state, items) {
    items.forEach(i => {
      addRel(state, i.board, i.user);
    });
  },
  ADD_REL(state, { board, user }) {
    addRel(state, board, user);
  },
  DELETE_REL(state, { board, user }) {
    deleteRel(state, board, user);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
